/**
 * pw-session-guard.js
 * ----------------------------------------------------------------------------
 * Self-heal for a Multipass session lost right after login.
 *
 * Root cause (established from 21 HAR captures): Multipass mints the session
 * on one Shopify datacenter, and Shopify's edge can route the next storefront
 * requests to another datacenter that has not replicated it yet. That origin
 * renders the customer as a guest and its response overwrites the HttpOnly
 * _shopify_essential cookie, so the browser is logged out with no logout
 * request ever made. It is visible within ~1.7s of landing, permanent once the
 * cookie is overwritten, and cannot be waited out — only a fresh login fixes
 * it. Nothing theme-side can choose the datacenter.
 *
 * Recovery = re-enter the existing login flow through /pages/authenticate with
 * the stored LMS token (the backend accepts a replayed token). It is a
 * NAVIGATION, not a fetch: a tab that has flipped to the cold datacenter has
 * its fetch/XHR pinned there, and that origin does not even recognise the
 * /apps/... app-proxy route. Top-level navigations are routed afresh.
 * sections/loader.liquid reads the params and pwlive-syncup.js runs end to end.
 *
 * When to recover (DEFER_TO_NEXT_NAVIGATION):
 *   true  — detect early, but do not yank the page away. Mark the session
 *           dead and heal on the user's next same-origin navigation: the
 *           click is intercepted, the user goes through /pages/authenticate
 *           with redirect_url = where they were going, and arrives logged in.
 *           Cart/checkout triggers (HEAL_FIRST_SELECTORS) heal first and
 *           return to the same page, because a side cart or checkout opened
 *           on a dead session would run as a guest.
 *   false — reload into the login flow the moment death is detected.
 * Immediate in both modes: a page that rendered as a guest inside the
 * post-login window (the landing itself hit the cold replica), and any page
 * that renders guest while the session is known-dead — the safety net for
 * navigations the interceptor cannot see (back/forward, typed URLs).
 *
 * Attempt accounting: the guard marks `pending` and navigates; loader.liquid
 * converts `pending` -> `attempted` only when the re-entry actually arrives,
 * so a navigation cancelled by a user tap does not burn the attempt. One
 * completed re-entry per session, hard cap of MAX_NAVS navigations started.
 * A false-positive re-entry is benign: the authenticate page then renders
 * with the customer and pwlive-syncup.js bounces straight back.
 *
 * Every decision is logged under [pw-auth].
 */
(function () {
  'use strict';

  var TAG = '[pw-auth]';
  var DEFER_TO_NEXT_NAVIGATION = true;

  var PENDING = 'pw_session_reseed_pending';     // set here before navigating; cleared by loader.liquid on arrival
  var ATTEMPTED = 'pw_session_reseed_attempted'; // set by loader.liquid when a re-entry actually arrives
  var NAV_COUNT = 'pw_session_reseed_nav_count'; // navigations started; hard cap
  var DEAD = 'pw_session_dead';                  // session known dead; heal on next navigation / next guest render
  var RESUME = 'pw_session_resume_url';          // full path+query the user was heading to when we healed
  var MAX_NAVS = 2;
  var PROBE_SECTION = 'session-probe';
  var PROBE_SCHEDULE_MS = [300, 1800, 3500];     // after load

  // Same-origin controls that act on the cart/session without navigating.
  // Extend once GoKwik's injected buttons are known.
  var HEAL_FIRST_SELECTORS = [
    '#gokwik-buy-now', '[data-gokwik-button]',              // PDP Buy Now (snippets/gokwik-buy-now.liquid)
    '[onclick*="openGokwikSideCart"]', '.header-actions__bag', // header bag icon (snippets/header-actions.liquid)
    '.mobile-sticky-cart-button__trigger',                    // mobile sticky cart (snippets/mobile-sticky-cart-button.liquid)
    '[onclick*="tryOpenSideCart"]',                           // /cart redirect flow (snippets/cart-redirection.liquid)
    'a[href="/cart"]', 'a[href^="/cart?"]', 'a[href^="/checkout"]',
    '#checkout', '[name="checkout"]',                         // cart page (snippets/cart-summary.liquid)
    '[data-gokwik-checkout]', '.gokwik-checkout',             // GoKwik side-cart checkout — confirm in the live DOM
  ].join(',');
  var BUY_NOW_SELECTOR = '#gokwik-buy-now, [data-gokwik-button]';
  // Cart-open controls. The theme already reopens the cart after a login when
  // sessionStorage.pwstOpenCartAfterLogin is '1' (header-actions.liquid sets it
  // for the logged-out bag button; login-popup-handler.js acts on it at
  // DOMContentLoaded). Set it before healing so the bag opens by itself on
  // return — no second tap.
  var CART_OPEN_SELECTOR = [
    '[onclick*="openGokwikSideCart"]', '.header-actions__bag',
    '.mobile-sticky-cart-button__trigger', '[onclick*="tryOpenSideCart"]',
    'a[href="/cart"]', 'a[href^="/cart?"]',
  ].join(',');
  // gokwik-buy-now.liquid resumes a pending Buy Now after login: it reads
  // sessionStorage.pwPendingBuyNow on DOMContentLoaded and clicks the button.
  // Stash the selection the same way before healing so the user lands back on
  // the PDP logged in and the checkout opens without a second tap.
  var BUY_NOW_FORM = "form[id^='BuyButtons-ProductForm'], form[data-type='add-to-cart-form']";

  var path = window.location.pathname;
  if (path === '/pages/authenticate') return;

  function ss(key) { try { return window.sessionStorage.getItem(key); } catch (e) { return null; } }
  function ssSet(key, value) { try { window.sessionStorage.setItem(key, value); } catch (e) {} }
  function ssDel(key) { try { window.sessionStorage.removeItem(key); } catch (e) {} }
  function ls(key) { try { return window.localStorage.getItem(key); } catch (e) { return null; } }
  function log() { try { console.warn.apply(console, [TAG].concat([].slice.call(arguments))); } catch (e) {} }

  var pm = window.pwPostMultipass || { active: false, sinceLogin: Infinity };
  var here = path + window.location.search;

  // 0. We just healed and the user was heading somewhere specific: take them
  //    there. (The backend composes the landing URL itself, so the exact query
  //    is restored here rather than passed through redirect_url.)
  var resume = ss(RESUME);
  if (resume) {
    ssDel(RESUME);
    if (window.hasCustomer === true && resume !== here) {
      var rp = resume.split('?')[0];
      var rq = resume.indexOf('?') !== -1 ? resume.slice(resume.indexOf('?')) : '';
      if (rp !== path || (rq && rq !== window.location.search)) {
        log('healed; resuming the navigation the user made', resume);
        window.location.replace(resume);
        return;
      }
    }
  }

  var deadKnown = ss(DEAD) === 'true';
  if (!pm.active && !deadKnown) return;          // ordinary page, nothing to do
  if (ss(ATTEMPTED) === 'true') return;           // one completed re-entry per session
  if (ss('force_logout') === 'true' || ss('pw_logout_in_progress') === 'true') return;
  var navCount = parseInt(ss(NAV_COUNT) || '0', 10) || 0;
  if (navCount >= MAX_NAVS) {
    log('re-entry navigation cap reached; not retrying', { navCount: navCount, path: path });
    return;
  }

  function readContext() {
    try {
      var c = JSON.parse(ls('STORE_TOKEN_CONTEXT') || 'null');
      return c && c.token && c.randomId ? c : null;
    } catch (e) { return null; }
  }

  /** 'dead' | 'alive' | null (inconclusive). Section wrapper tags are stripped. */
  function probe() {
    return fetch(path + '?section_id=' + PROBE_SECTION, { cache: 'no-store', credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.text() : null; })
      .then(function (t) {
        if (t == null) return null;
        var v = t.replace(/<[^>]*>/g, '').trim();
        if (v.charAt(0) === '0') return 'dead';
        if (v.charAt(0) === '1') return 'alive';
        return null;
      })
      .catch(function () { return null; });
  }

  // Where to land after re-login when no destination was given: the page the
  // user is on, or their account if the dead session bounced them to login.
  function targetPath() {
    if (/^\/account\/(login|register)/.test(path)) return '/account';
    return path;
  }

  /**
   * Re-enter the login flow. `dest` (path+query) is where the user should end
   * up; only its path goes through redirect_url, the full value is restored
   * by step 0 on arrival.
   */
  function reseed(reason, dest) {
    var ctx = readContext();
    if (!ctx) {
      log('session lost after Multipass login but no stored token; cannot re-enter', reason);
      return;
    }
    dest = dest || targetPath();
    var destPath = dest.split('#')[0].split('?')[0] || '/';
    ssSet(PENDING, 'true');
    ssSet(NAV_COUNT, String(navCount + 1));
    ssSet(RESUME, dest.split('#')[0]);
    log('re-entering /pages/authenticate', reason, { to: dest });

    var params = new URLSearchParams();
    params.set('token', ctx.token);
    params.set('random_id', ctx.randomId);
    params.set('redirect_url', destPath);
    if (ss('fromApp') === 'true') params.set('fromApp', 'true');
    window.location.replace('/pages/authenticate?' + params.toString());
  }

  // Case 1: rendered as a guest. Inside the post-login window that means the
  // landing itself hit a cold replica; with a known-dead session it means the
  // user navigated somewhere the interceptor did not see. Heal now.
  if (window.hasCustomer !== true) {
    reseed({ why: deadKnown ? 'known-dead session; page rendered guest' : 'landing rendered guest', path: path, msSinceLogin: pm.sinceLogin });
    return;
  }

  // Rendered logged in. Stale markers from an earlier alarm are cleared; the
  // probes below re-verify.
  if (deadKnown) ssDel(DEAD);
  if (ss(PENDING) === 'true') ssDel(PENDING);

  function stashPendingBuyNow() {
    try {
      var form = document.querySelector(BUY_NOW_FORM);
      var id = form && form.querySelector('[name="id"]');
      if (!id || !id.value) return;
      var qty = form.querySelector('[name="quantity"]');
      window.sessionStorage.setItem('pwPendingBuyNow', JSON.stringify({
        variantId: id.value,
        quantity: (qty && qty.value) || '1',
      }));
    } catch (e) {}
  }

  var armed = false;
  function armInterceptor() {
    if (armed) return;
    armed = true;

    document.addEventListener('click', function (ev) {
      if (ev.defaultPrevented || ev.button !== 0 || ev.metaKey || ev.ctrlKey || ev.shiftKey || ev.altKey) return;
      var el = ev.target;
      if (!el || !el.closest) return;

      // Cart / checkout controls: heal first, come back to this page.
      var healFirst = el.closest(HEAL_FIRST_SELECTORS);
      if (healFirst) {
        ev.preventDefault();
        ev.stopPropagation();
        if (healFirst.matches(BUY_NOW_SELECTOR)) stashPendingBuyNow();
        else if (healFirst.matches(CART_OPEN_SELECTOR)) ssSet('pwstOpenCartAfterLogin', '1');
        reseed({ why: 'deferred heal before cart/checkout action', control: healFirst.id || healFirst.className || healFirst.tagName }, here);
        return;
      }

      var a = el.closest('a[href]');
      if (!a || a.hasAttribute('download')) return;
      var target = (a.getAttribute('target') || '').toLowerCase();
      if (target && target !== '_self') return;
      var href = a.getAttribute('href') || '';
      if (/^\s*(#|javascript:|mailto:|tel:|sms:)/i.test(href)) return;
      var u;
      try { u = new URL(a.href, window.location.href); } catch (e) { return; }
      if (u.origin !== window.location.origin) return;
      if (u.pathname === '/pages/authenticate') return;
      if (u.pathname === path && u.search === window.location.search && u.hash) return; // in-page anchor

      ev.preventDefault();
      ev.stopPropagation();
      reseed({ why: 'deferred heal on user navigation' }, u.pathname + u.search);
    }, true);

    document.addEventListener('submit', function (ev) {
      if (ev.defaultPrevented) return;
      var f = ev.target;
      if (!(f instanceof HTMLFormElement)) return;
      if ((f.getAttribute('method') || 'get').toLowerCase() !== 'get') return;
      var u;
      try { u = new URL(f.getAttribute('action') || window.location.href, window.location.href); } catch (e) { return; }
      if (u.origin !== window.location.origin) return;
      var q = new URLSearchParams(new FormData(f)).toString();
      u.search = q ? '?' + q : '';
      ev.preventDefault();
      ev.stopPropagation();
      reseed({ why: 'deferred heal on form navigation' }, u.pathname + u.search);
    }, true);
  }

  function onDead(reason) {
    if (!DEFER_TO_NEXT_NAVIGATION) { reseed(reason); return; }
    ssSet(DEAD, 'true');
    log('session lost after Multipass login; will re-authenticate on the next navigation', reason);
    armInterceptor();
  }

  // Probe early: death is visible ~1.7s after landing and never flaps, so the
  // first clear "0" is final; two clear "1"s end it.
  function schedule() {
    var i = 0;
    var aliveCount = 0;
    function next() {
      if (i >= PROBE_SCHEDULE_MS.length) return;
      var wait = i === 0 ? PROBE_SCHEDULE_MS[0] : PROBE_SCHEDULE_MS[i] - PROBE_SCHEDULE_MS[i - 1];
      i++;
      setTimeout(function () {
        probe().then(function (r) {
          if (r === 'dead') return onDead({ why: 'probe ' + i + ' dead', path: path });
          if (r === 'alive' && ++aliveCount >= 2) return;
          next();
        });
      }, wait);
    }
    next();
  }

  // Schedule from DOMContentLoaded, not load: on the 1.2MB landing page `load`
  // waits for every image and can arrive after the user's first tap, which
  // would leave the interceptor unarmed. Death is visible ~1.7s after the
  // landing request, which DOMContentLoaded + 300ms comfortably clears.
  if (document.readyState !== 'loading') schedule();
  else document.addEventListener('DOMContentLoaded', schedule, { once: true });
})();
